import React, { useCallback, useEffect, useMemo } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ContentCopyOutlined } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import ApiOutAccessB2b, { IApiOutAccessB2b } from 'api/b2b/ApiOutAccessManag'
import B2bTDPAPI from 'api/b2b/TdpApi'
import { findByValue } from 'helpers/b2b'
import OrbitSession from 'hooks/useCookies'

import { environmentOptions, ipWhitelistOptions } from '../constants'

// Type definitions
interface TenantOption {
  label: string
  value: string
  agencyId: string
}

interface Company {
  tenant_id: string
  company_name: string
  id: string
}

interface TenantData {
  data: Company[]
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface FormData {
  tenantName: SelectOption | null
  agencyID: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  environment: any
  apiKey: string
  apiSecret: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  whitelistType: any
  ipRanges: Array<{ from: string; to: string }>
  singleIPs: string
}
type SelectOption = { label: string; value: string; agencyId: string }

function AddAPIKey() {
  const { control, watch, setValue, handleSubmit, reset } = useForm<FormData>({
    defaultValues: {
      tenantName: null,
      agencyID: '',
      environment: environmentOptions[0],
      apiKey: '',
      apiSecret: '',
      whitelistType: ipWhitelistOptions[0],
      ipRanges: [{ from: '', to: '' }],
      singleIPs: ''
    }
  })
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const mode = searchParams.get('mode')
  const companyId = searchParams.get('id') || ''
  const token = OrbitSession.get('faberToken')

  const shouldFetchApiKeyDetails = Boolean(mode && companyId)

  // This will only work if the mode is edit and id is present
  const { data: apiKeyDetails } = useQuery(
    ['apiKeyDetails', mode, companyId],
    async () => {
      const response = await ApiOutAccessB2b.getTenantAccessInfo(
        token,
        companyId
      )
      return response.data[0]
    },
    {
      enabled: shouldFetchApiKeyDetails,
      refetchOnMount: 'always',
      onError: () => {
        toast.error('Error searching technology companies')
      }
    }
  )

  const {
    // status,
    data: tenantData,
    isLoading: isTenantLoading,
    isError: tenantFetchError,
    refetch: refetchTenant,
    isFetching: isTenantFetching
  } = useQuery<TenantData>(
    ['tenant-detail'],
    async () => {
      const { data } = await B2bTDPAPI.getTDPDetails(token, {
        tenant_id: companyId
      })
      return data
    },
    {
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
      refetchOnWindowFocus: false
    }
  )

  const { mutate: generateKey, isLoading: isGeneratingKey } = useMutation({
    mutationFn: async () => {
      return ApiOutAccessB2b.generateApiAccessTokenB2b(token)
    },
    onSuccess: (data) => {
      if (data?.status === 'success' && data?.data) {
        setValue('apiKey', data.data.api_key)
        setValue('apiSecret', data.data.api_secret)
        toast.success('API key and secret generated successfully')
      }
    },
    onError: (error) => {
      console.error('Generate API key error:', error)
      toast.error('Failed to generate API key and secret')
    }
  })

  const formatIps = (formData: FormData) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let ipsArray: any[] = []
    if (formData.whitelistType?.value === ipWhitelistOptions[0].value) {
      // For single IPs, split by comma and create objects with Type: "Specific"
      const singleIPs = formData.singleIPs
        .split(',')
        .map((ip) => ip.trim())
        .filter((ip) => ip)
      ipsArray = singleIPs.map((ip) => ({
        From: ip,
        Type: 'Specific'
      }))
    } else if (formData.whitelistType?.value === ipWhitelistOptions[1].value) {
      // For IP ranges, create objects with Type: "Range"
      const validRanges = formData.ipRanges.filter(
        (range) => range.from && range.to
      )
      ipsArray = validRanges.map((range) => ({
        From: range.from,
        To: range.to,
        Type: 'Range'
      }))
    }
    return ipsArray.length > 0 ? ipsArray : null
  }

  const { mutate: addTenantAccess, isLoading: isAddingAccess } = useMutation({
    mutationFn: async (formData: FormData) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ipsArray: any[] | null = formatIps(formData)
      const apiPayload: IApiOutAccessB2b = {
        is_active: true,
        created_by: OrbitSession.get('email') || '',
        tenant_id: formData.agencyID,
        api_key: formData.apiKey,
        api_secret: formData.apiSecret,
        access_type: formData.environment.value,
        ips: ipsArray !== null ? JSON.stringify(ipsArray) : null
      }
      return ApiOutAccessB2b.addTenantAccess(token, apiPayload)
    },
    onSuccess: () => {
      toast.success('Tenant access added successfully')
      navigate('/hq/b2b/api-out-access')
    },
    onError: (error) => {
      toast.error('Failed to add tenant access')
      console.error('Add tenant access error:', error)
    }
  })
  const { mutate: updateTenantAccessData } = useMutation({
    mutationFn: async (formData: FormData) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ipsArray: any[] | null = formatIps(formData)
      const apiPayload: IApiOutAccessB2b = {
        is_active: true,
        access_type: formData.environment.value,
        created_by: OrbitSession.get('email') || '',
        tenant_id: formData.agencyID,
        id: formData.agencyID,
        tdp_api_master_id: apiKeyDetails.tdp_api_master_id,
        api_key: formData.apiKey,
        api_secret: formData.apiSecret,
        ips: ipsArray !== null ? JSON.stringify(ipsArray) : null,
        updated_by: OrbitSession.get('email') || ''
      }
      return ApiOutAccessB2b.updateTenantAccessData(token, apiPayload)
    },
    onSuccess: () => {
      toast.success('Tenant access added successfully')
      navigate('/hq/b2b/api-out-access')
    },
    onError: (error) => {
      toast.error('Failed to add tenant access')
      console.error('Add tenant access error:', error)
    }
  })

  const tenantOptions = useMemo((): SelectOption[] => {
    if (!tenantData || !Array.isArray(tenantData)) return []
    return tenantData.slice(0, 1000).map((company: Company) => ({
      label: company.company_name || '',
      value: company.company_name || '',
      agencyId: company.id || ''
    }))
  }, [tenantData])

  const whitelistType = watch('whitelistType')
  const tenantNameInput = watch('tenantName')
  const hasApiKey = watch('apiKey')
  const hasApiSecret = watch('apiSecret')

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'ipRanges'
  })

  // Auto-populate agency ID when tenant is selected
  useEffect(() => {
    if (tenantNameInput?.agencyId) {
      setValue('agencyID', tenantNameInput.agencyId)
    } else {
      setValue('agencyID', '')
    }
  }, [tenantNameInput, setValue])

  // const findByValue = (opts: SelectOption[], v?: string | null) =>
  //   opts.find((o) => o.value === v) ?? undefined

  // Fixed useEffect for edit mode
  useEffect(() => {
    if (apiKeyDetails && tenantOptions.length > 0) {
      // Find tenant by matching tenant_id with value (which is now company.id)
      const selectedTenant = tenantOptions.find(
        (option) => option.agencyId === apiKeyDetails.tenant_id
      )
      // Parsing the ip's
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let normalizedIps: any = []

      if (apiKeyDetails?.ips !== '') {
        normalizedIps = Array.isArray(apiKeyDetails?.ips)
          ? apiKeyDetails.ips
          : JSON.parse(apiKeyDetails.ips)
      }

      reset({
        tenantName: selectedTenant || null,
        agencyID: apiKeyDetails.tenant_id || '',
        apiKey: apiKeyDetails.api_key || '',
        apiSecret: apiKeyDetails.api_secret || '',
        environment: environmentOptions[0],
        whitelistType: normalizedIps.length
          ? findByValue(ipWhitelistOptions, normalizedIps[0]?.Type)
          : ipWhitelistOptions,
        ipRanges: [{ from: '', to: '' }],
        singleIPs: ''
      })
      // Filling the form for range IPs
      const rangeRows = normalizedIps
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((x: any) => x?.Type === 'Range')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((x: any) => ({ from: x?.From ?? '', to: x?.To ?? '' }))

      replace(rangeRows.length ? rangeRows : [{ from: '', to: '' }])

      // Filling the form for single IPs
      const specific = normalizedIps
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((x: any) => x?.Type === 'Specific')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((x: any) => x?.From)
        .filter(Boolean)
        .join(', ')
      setValue('singleIPs', specific)
    }
  }, [apiKeyDetails, tenantOptions, reset])

  // Optimized autocomplete filter function
  const filterOptions = useCallback(
    (options: TenantOption[], { inputValue }: { inputValue: string }) => {
      if (!inputValue) return options.slice(0, 100) // Limit initial results

      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      )
      return filtered.slice(0, 100) // Limit filtered results
    },
    []
  )

  // Memoized IP range button handler
  const handleIPRangeButton = useCallback(
    (index: number) => {
      if (fields.length - 1 === index && index !== 0) {
        remove(index)
      } else {
        append({ from: '', to: '' })
      }
    },
    [fields.length, append, remove]
  )

  // Form submit handler
  const onSubmit = (data: FormData) => {
    if (!data.apiKey || !data.apiSecret) {
      toast.error('Please generate API key and secret first')
      return
    }

    if (data.whitelistType?.value === ipWhitelistOptions[0].value) {
      const singleIPs = data.singleIPs
        .split(',')
        .map((ip) => ip.trim())
        .filter((ip) => ip)
      if (singleIPs.length === 0) {
        toast.error('Please enter at least one IP address')
        return
      }
    }

    if (data.whitelistType?.value === ipWhitelistOptions[1].value) {
      const validRanges = data.ipRanges.filter(
        (range) => range.from && range.to
      )
      if (validRanges.length === 0) {
        toast.error('Please enter at least one IP range')
        return
      }
    }
    if (mode !== 'edit' && !companyId) addTenantAccess(data)
    else updateTenantAccessData(data)
  }

  if (isTenantFetching) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading tenant data...</Typography>
      </Box>
    )
  }

  if (tenantFetchError) {
    return (
      <Box>
        <Typography color='error'>Failed to load tenant data</Typography>
        <Button
          onClick={() => refetchTenant()}
          variant='outlined'
          sx={{ mt: 1 }}
        >
          Retry
        </Button>
      </Box>
    )
  }

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)}>
      <Box display='flex' alignItems='center' sx={{ mb: 2 }}>
        <Typography sx={{ mr: '10px', fontSize: '14px', minWidth: '120px' }}>
          Tenant Name*
        </Typography>

        <Controller
          name='tenantName'
          control={control}
          rules={{ required: { value: true, message: 'Required' } }}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete<SelectOption, false, false, false>
              disablePortal
              // disableClearable={mode === 'edit' || true} // Only disable clearable in edit mode
              disabled={mode === 'edit'} // Disable the entire field in edit mode
              options={tenantOptions}
              loading={isTenantLoading}
              getOptionLabel={(o) => o?.label ?? ''}
              isOptionEqualToValue={(a, b) => a?.value === b?.value}
              value={field.value ?? null} // Use null for empty value in create mode
              onChange={(_, opt) => field.onChange(opt)}
              size='small'
              sx={{ width: '300px' }}
              filterOptions={filterOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!error}
                  helperText={error?.message}
                  placeholder={
                    mode === 'create' ? 'Select tenant...' : undefined
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isTenantFetching ? (
                          <CircularProgress size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          )}
        />
      </Box>

      {tenantNameInput?.value && (
        <>
          <Box display='flex' alignItems='center' sx={{ mb: 2 }}>
            <Typography
              sx={{ mr: '10px', fontSize: '14px', minWidth: '120px' }}
            >
              Agency ID
            </Typography>
            <Controller
              name='agencyID'
              control={control}
              rules={{ required: { value: true, message: 'Required' } }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  id='agencyID'
                  variant='outlined'
                  size='small'
                  disabled={true}
                  error={!!error}
                  helperText={error?.message}
                  sx={{ width: '300px' }}
                  {...field}
                />
              )}
            />
          </Box>

          <Box display='flex' alignItems='center' sx={{ mb: 2 }}>
            <Typography
              sx={{ mr: '10px', fontSize: '14px', minWidth: '120px' }}
            >
              Environment*
            </Typography>
            <Controller
              name='environment'
              control={control}
              rules={{ required: { value: true, message: 'Required' } }}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  disablePortal
                  options={environmentOptions}
                  disabled={mode === 'edit'}
                  getOptionLabel={(option) => option.label || ''}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  onChange={(_, data) => field.onChange(data)}
                  size='small'
                  sx={{ width: '300px' }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              )}
            />
          </Box>

          <Box display='flex' alignItems='center' sx={{ mb: 2 }}>
            <Typography
              sx={{ mr: '10px', fontSize: '14px', minWidth: '120px' }}
            >
              API Key
            </Typography>
            <Controller
              name='apiKey'
              control={control}
              rules={{ required: { value: true, message: 'Required' } }}
              render={({ field, fieldState: { error } }) => (
                <Box display='flex' alignItems='center' sx={{ width: '400px' }}>
                  <TextField
                    {...field}
                    id='apiKey'
                    variant='outlined'
                    size='small'
                    disabled
                    error={!!error}
                    helperText={error?.message}
                    sx={{ flexGrow: 1 }}
                  />
                  <IconButton
                    size='small'
                    onClick={() => {
                      navigator.clipboard.writeText(field.value || '')
                      toast.success('API Key copied!')
                    }}
                  >
                    <ContentCopyOutlined fontSize='small' />
                  </IconButton>
                </Box>
              )}
            />
          </Box>

          <Box display='flex' alignItems='center' sx={{ mb: 2 }}>
            <Typography
              sx={{ mr: '10px', fontSize: '14px', minWidth: '120px' }}
            >
              API Secret
            </Typography>
            <Controller
              name='apiSecret'
              control={control}
              rules={{ required: { value: true, message: 'Required' } }}
              render={({ field, fieldState: { error } }) => (
                <Box display='flex' alignItems='center' sx={{ width: '400px' }}>
                  <TextField
                    {...field}
                    id='apiSecret'
                    variant='outlined'
                    size='small'
                    disabled
                    error={!!error}
                    helperText={error?.message}
                    sx={{ flexGrow: 1 }}
                  />
                  <IconButton
                    size='small'
                    onClick={() => {
                      navigator.clipboard.writeText(field.value || '')
                      toast.success('API Secret copied!')
                    }}
                  >
                    <ContentCopyOutlined fontSize='small' />
                  </IconButton>
                </Box>
              )}
            />
          </Box>
        </>
      )}

      <Button
        disabled={!tenantNameInput?.value || isGeneratingKey || mode === 'edit'}
        onClick={() => generateKey()}
        variant='contained'
        sx={{ mb: 2 }}
      >
        {isGeneratingKey ? 'Generating...' : 'Generate'}
      </Button>

      <Divider sx={{ m: '20px 0' }} />

      <Typography variant='h4' sx={{ mr: '10px', mb: 2 }}>
        Network Access
      </Typography>

      <Box display='flex' alignItems='center' sx={{ mb: 2 }}>
        <Typography sx={{ mr: '10px', fontSize: '14px', minWidth: '120px' }}>
          IP Whitelist
        </Typography>
        <Controller
          name='whitelistType'
          control={control}
          // rules={{ required: { value: true, message: 'Required' } }}
          render={({ field }) => (
            <Autocomplete
              {...field}
              disablePortal
              options={ipWhitelistOptions}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              onChange={(_, data) => field.onChange(data)}
              size='small'
              sx={{ width: '300px' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  // error={!!error}
                  // helperText={error?.message}
                />
              )}
            />
          )}
        />
      </Box>

      {whitelistType?.value === 'Specific' && (
        <Box display='flex' alignItems='center' sx={{ mb: 2 }}>
          <Typography sx={{ mr: '10px', fontSize: '14px', minWidth: '120px' }}>
            IPs (comma separated)*
          </Typography>
          <Controller
            name='singleIPs'
            control={control}
            rules={{ required: { value: true, message: 'Required' } }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                id='singleIPs'
                variant='outlined'
                size='small'
                error={!!error}
                helperText={error?.message}
                sx={{ width: '300px' }}
                placeholder='192.168.1.1, 192.168.1.2'
                {...field}
              />
            )}
          />
        </Box>
      )}

      {whitelistType?.value === 'Range' && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '14px', mb: 1 }}>
            Input IP Ranges*
          </Typography>
          {fields?.map((item, index) => (
            <Box
              key={item.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                gap: 2
              }}
            >
              <Controller
                name={`ipRanges.${index}.from`}
                control={control}
                rules={{ required: { value: true, message: 'Required' } }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id={`ipRanges.${index}.from`}
                    variant='outlined'
                    size='small'
                    label='From'
                    placeholder='192.168.1.1'
                    error={!!error}
                    helperText={error?.message}
                    sx={{ width: '150px' }}
                    {...field}
                  />
                )}
              />
              <Controller
                name={`ipRanges.${index}.to`}
                control={control}
                rules={{ required: { value: true, message: 'Required' } }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id={`ipRanges.${index}.to`}
                    variant='outlined'
                    size='small'
                    label='To'
                    placeholder='192.168.1.100'
                    error={!!error}
                    helperText={error?.message}
                    sx={{ width: '150px' }}
                    {...field}
                  />
                )}
              />
              <Button
                onClick={() => handleIPRangeButton(index)}
                variant='outlined'
                size='small'
                sx={{ minWidth: '40px', height: '40px' }}
              >
                {fields.length - 1 === index && index !== 0 ? '−' : '+'}
              </Button>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ mt: '20px' }}>
        <Button
          type='submit'
          variant='contained'
          disableElevation
          disabled={isAddingAccess || !hasApiKey || !hasApiSecret}
        >
          {isAddingAccess ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Box>
  )
}

export default AddAPIKey
